import React, { useEffect, useMemo } from "react";
import { Box, Text, Stack, useColorModeValue } from "@chakra-ui/react";
import type { FunctionComponent } from "react";
import type { StockTickerData } from "api/stockTickerData";
import { useCollectionsStocksContext } from "screens/landing/tabs/collections/CollectionsStocksProvider";

interface Props {
  ticker?: string;
  exchange?: string;
}

export const StockIndicatorSmall: FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({ ticker, exchange }) => {
  const { fetchStockData, getStockData } = useCollectionsStocksContext();
  const subTitlecolor = useColorModeValue("white", "gray.300");
  const iconBgColor = useColorModeValue("400", "500");
  const iconBgColorNoValue = useColorModeValue("200", "500");
  const iconColor = useColorModeValue("400", "400");

  const areTickerAndExchangePresent = ticker && exchange;

  const stockData: StockTickerData | undefined = useMemo(() => {
    if (!areTickerAndExchangePresent) return;

    return getStockData(ticker, exchange);
  }, [exchange, ticker, areTickerAndExchangePresent, getStockData]);

  useEffect(() => {
    if (stockData) return;
    if (!areTickerAndExchangePresent) return;

    fetchStockData(ticker, exchange);
  }, [areTickerAndExchangePresent, exchange, fetchStockData, stockData, ticker]);

  const currentPrice = Number(stockData?.ticker_quote?.price ? stockData?.ticker_quote?.price : 0);
  const previousClose = Number(stockData?.ticker_quote?.previous_close ? stockData?.ticker_quote?.previous_close : 0);
  const difference = stockData?.ticker_quote?.price ? (Number(stockData.ticker_quote.price) - previousClose).toFixed(2) : "0.00";
  const percentChange = stockData?.ticker_quote?.percent_change;
  const percentageChange = percentChange ? `${Number(percentChange.replace("%", "")).toFixed(2)}%` : "--";

  return (
    <Stack direction="row" spacing=".5rem" justifyContent={"flex-end"} alignItems="center" width="100%" minWidth="7rem">
      <Box
        height={"100%"}
        px=".5rem"
        borderRadius="md"
        opacity={1}
        bgColor={
          percentageChange.includes("--")
            ? `gray.${iconBgColorNoValue}`
            : percentageChange.includes("-")
            ? `red.${iconBgColor}`
            : `green.${iconBgColor}`
        }>
        <Text className="ch-project-stock-indicator-current-price" fontSize={"md"} color={subTitlecolor} fontWeight="semibold">
          {currentPrice === 0 ? "--" : currentPrice.toFixed(2)}
        </Text>
      </Box>

      <Stack justifyContent={"end"} spacing="0" fontSize={"11px"} lineHeight={"12px"}>
        <Text
          className="ch-project-stock-indicator-percentage"
          textAlign={"right"}
          fontWeight="semibold"
          color={percentageChange.includes("-") ? `red.${iconColor}` : `green.${iconColor}`}>
          {percentageChange === "NaN%" ? "--" : percentageChange}
        </Text>
        <Text
          className="ch-project-stock-indicator-difference"
          textAlign={"right"}
          fontWeight="semibold"
          color={difference.includes("-") ? `red.${iconColor}` : `green.${iconColor}`}>
          {difference === "0.00" ? "--" : difference}
        </Text>
      </Stack>
    </Stack>
  );
};
